import { TradutorAssistente } from './assistente.interface'

/**
 * QW10 — a metade de apresentação do reconhecimento verbal (régua R6).
 *
 * O backend já provou o que interessa e não é reaproveitado aqui: cada valor
 * de `dados_reconhecidos` está na FALA da pessoa (o servidor restrito do
 * runtime recusa o resto). Este módulo é só a frase — e é pequeno de
 * propósito, porque a frase é o produto inteiro deste degrau.
 *
 * O achado C16: "cadastra a colaboradora VALIDACAO-UX-Maria Souza..." abre
 * `/employee/new` com o nome vazio e a mensagem-padrão. O mesmo padrão em C5
 * ("para o colaborador Pedro" evapora) e C14 ("Setembro" e "um colaborador"
 * descartados). O custo medido não é redigitar; é a dúvida — "ele entendeu
 * quem eu quero cadastrar?".
 *
 * A frase responde exatamente essa dúvida e nada além dela: devolve o dado
 * para conferir e copiar. Quando há rascunho, a frase só descreve o que ficou
 * de fora; a barra do painel é quem afirma o que de fato foi levado.
 */

export interface DadoReconhecido {
  rotulo: string
  valor: string
}

/**
 * Separador entre os pares na frase. Ponto-e-vírgula porque `rotulo: valor` já
 * usa dois-pontos, e vírgula ali leria como se o valor continuasse.
 */
const SEPARADOR = '; '

function temRascunho (rascunhoPresente: boolean | string | null | undefined): boolean {
  return typeof rascunhoPresente === 'boolean'
    ? rascunhoPresente
    : typeof rascunhoPresente === 'string'
      ? rascunhoPresente.trim().length > 0
      : false
}

/**
 * A frase de reconhecimento, ou `null` quando não há o que reconhecer —
 * ausência tem de continuar sendo silêncio, não uma frase vazia grudada na
 * navegação de todo mundo.
 */
export function fraseDeReconhecimento (
  dados: DadoReconhecido[] | null | undefined,
  traduzir: TradutorAssistente,
  rascunhoPresente: boolean | string | null | undefined = false
): string | null {
  if (!dados || !dados.length) return null

  const pares = dados
    .filter(dado => dado && dado.rotulo?.trim() && dado.valor?.trim())
    .map(dado => `${dado.rotulo.trim()}: ${dado.valor.trim()}`)
  if (!pares.length) return null

  return traduzir(
    temRascunho(rascunhoPresente)
    ? 'assistentePontotel.conversa.dadosReconhecidosParcial'
      : 'assistentePontotel.conversa.dadosReconhecidos',
    {
      dados: pares.join(SEPARADOR)
    }
  )
}

/**
 * A frase de navegação com o reconhecimento colado, quando há reconhecimento.
 * Duas frases, uma bolha: a leitura de "o que aconteceu" e a de "o que eu
 * ouvi e não levei" pertencem ao mesmo turno, e separá-las em duas bolhas
 * faria a segunda parecer um evento novo.
 */
export function textoDaNavegacaoComReconhecimento (
  textoDaNavegacao: string,
  dados: DadoReconhecido[] | null | undefined,
  traduzir: TradutorAssistente,
  rascunhoPresente: boolean | string | null | undefined = false
): string {
  const frase = fraseDeReconhecimento(dados, traduzir, rascunhoPresente)
  return frase ? `${textoDaNavegacao} ${frase}` : textoDaNavegacao
}
